// frontend/src/pages/Notificaciones.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/Notificaciones.css';

const Notificaciones = () => {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas'); // 'todas', 'noLeidas', 'leidas'
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    noLeidas: 0,
    leidas: 0
  });

  // Cargar notificaciones
  const cargarNotificaciones = async () => {
    try {
      setLoading(true);
      let url = '/notificaciones?limit=100';
      
      if (filtro === 'noLeidas') {
        url = '/notificaciones?soloNoLeidas=true&limit=100';
      }
      
      const response = await api.get(url);
      
      if (response.data && response.data.data) {
        setNotificaciones(response.data.data);
        const noLeidas = response.data.noLeidas || response.data.data.filter(n => !n.leida).length;
        
        setEstadisticas({
          total: response.data.data.length,
          noLeidas: noLeidas,
          leidas: response.data.data.length - noLeidas
        });
      }
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  };

  // Marcar como leída
  const marcarComoLeida = async (id) => {
    try {
      await api.put(`/notificaciones/${id}/leer`);
      cargarNotificaciones();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Marcar todas como leídas
  const marcarTodasLeidas = async () => {
    try {
      await api.put('/notificaciones/marcar-todas');
      cargarNotificaciones();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Eliminar notificación (solo visible para admin)
  const eliminarNotificacion = async (id) => {
    if (!window.confirm('¿Eliminar esta notificación?')) return;
    
    try {
      await api.delete(`/notificaciones/${id}`);
      cargarNotificaciones();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    cargarNotificaciones();
  }, [filtro]);

  const getIcono = (notificacion) => {
    // Priorizar metadata.tipo si existe (para eventos)
    if (notificacion.metadata?.tipo) {
      switch (notificacion.metadata.tipo) {
        case 'vencimiento': return '⚠️';
        case 'reunion': return '👥';
        case 'capacitacion': return '📚';
        case 'evento': return '🎉';
        default: return '📅';
      }
    }
    
    // Si no, usar el tipo base
    switch (notificacion.tipo) {
      case 'exito': return '✅';
      case 'advertencia': return '⚠️';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  const getClasePrioridad = (prioridad) => {
    switch (prioridad) {
      case 'alta': return 'prioridad-alta';
      case 'media': return 'prioridad-media';
      default: return 'prioridad-baja';
    }
  };

  const formatFecha = (fecha) => {
    const ahora = new Date();
    const fechaObj = new Date(fecha);
    const diffMs = ahora - fechaObj;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Justo ahora';
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    
    return fechaObj.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="notificaciones-page">
      <div className="page-header">
        <div>
          <h1>Notificaciones</h1>
          <p>Historial de todas tus notificaciones</p>
        </div>
        {estadisticas.noLeidas > 0 && (
          <button className="btn-marcar-todas" onClick={marcarTodasLeidas}>
            Marcar todas como leídas
          </button>
        )}
      </div>

      {/* Resumen de estadísticas */}
      <div className="stats-notificaciones">
        <div className="stat-card-noti">
          <span className="stat-icon">📬</span>
          <div>
            <h3>{estadisticas.total}</h3>
            <p>Total</p>
          </div>
        </div>
        <div className="stat-card-noti no-leidas">
          <span className="stat-icon">🔴</span>
          <div>
            <h3>{estadisticas.noLeidas}</h3>
            <p>No leídas</p>
          </div>
        </div>
        <div className="stat-card-noti leidas">
          <span className="stat-icon">✓</span>
          <div>
            <h3>{estadisticas.leidas}</h3>
            <p>Leídas</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-notificaciones">
        <button 
          className={`filtro-btn ${filtro === 'todas' ? 'active' : ''}`}
          onClick={() => setFiltro('todas')}
        >
          Todas
        </button>
        <button 
          className={`filtro-btn ${filtro === 'noLeidas' ? 'active' : ''}`}
          onClick={() => setFiltro('noLeidas')}
        >
          No leídas
          {estadisticas.noLeidas > 0 && (
            <span className="contador">{estadisticas.noLeidas}</span>
          )}
        </button>
        <button 
          className={`filtro-btn ${filtro === 'leidas' ? 'active' : ''}`}
          onClick={() => setFiltro('leidas')}
        >
          Leídas
        </button>
      </div>

      {/* Lista de notificaciones */}
      <div className="lista-notificaciones">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando notificaciones...</p>
          </div>
        ) : notificaciones.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <h3>No hay notificaciones</h3>
            <p>Cuando tengas notificaciones, aparecerán aquí</p>
          </div>
        ) : (
          notificaciones.map(notif => (
            <div 
              key={notif._id} 
              className={`notificacion-card ${!notif.leida ? 'no-leida' : ''} ${getClasePrioridad(notif.prioridad)}`}
              onClick={() => !notif.leida && marcarComoLeida(notif._id)}
            >
              <div className="notif-icon">
                {getIcono(notif)}
              </div>
              <div className="notif-body">
                <div className="notif-header">
                  <h3>{notif.titulo}</h3>
                  <span className="notif-fecha">{formatFecha(notif.fecha)}</span>
                </div>
                <p className="notif-mensaje">{notif.mensaje}</p>
                {notif.metadata?.eventoId && (
                  <div className="notif-link">
                    <button 
                      className="btn-ver-evento"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = '/calendario';
                      }}
                    >
                      Ver evento →
                    </button>
                  </div>
                )}
                {notif.metadata?.documentoId && (
                  <div className="notif-link">
                    <button 
                      className="btn-ver-doc"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = '/documentos';
                      }}
                    >
                      Ver documento →
                    </button>
                  </div>
                )}
              </div>
              {!notif.leida && (
                <div className="notif-badge">
                  <span className="nuevo-punto"></span>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notificaciones;